import {
  DEFAULT_MEMORY_SIZE,
  GAME_KEYS,
  toReadingBand,
  type GameKey,
  type RoomRoundKey,
  type RoundOptions,
} from '@hazel/shared'

/**
 * How a room starts each game it can run.
 *
 * Every game carries its own settings — a reading band, a board size, three
 * race tiers — so the room's cards cannot be generated from a boolean. This is
 * that duplication made honest: a `Record<RoomRoundKey, …>`, so a game added
 * to the `RoomRound` union and to `roundOptions` fails the build here until
 * somebody says how a room is meant to start it.
 *
 * It used to be `HAS_A_BUTTON`, a record of `true`. That caught a game added
 * to the union with nothing here, but not one listed here and then forgotten
 * in the markup — a game that would compile, pass every test, and simply never
 * appear on the television. Saying *how* rather than *whether* closes that:
 * the cards are drawn from this, so listing a game is drawing it.
 *
 * Its own file rather than a constant beside the component, so the host screen
 * stays a file that exports only components and keeps its fast refresh.
 */
export type RoomStart =
  /** Starts straight away, on settings the room does not ask about. */
  | { readonly kind: 'straight'; readonly options: RoundOptions }
  /**
   * Asks how hard first.
   *
   * A room of cousins has no single skill level to be adaptive about, which is
   * why the race asks and the solo game does not.
   */
  | { readonly kind: 'tiers' }

/*
 * `toReadingBand(null)` is a pure call returning the default band, so it is
 * safe to settle here rather than at render.
 */
export const HOW_TO_START: Readonly<Record<RoomRoundKey, RoomStart>> = {
  hangman: { kind: 'straight', options: { game: 'hangman', band: toReadingBand(null) } },
  maths: { kind: 'tiers' },
  memory: { kind: 'straight', options: { game: 'memory', size: DEFAULT_MEMORY_SIZE } },
}

/**
 * The games a room can run, in the order the picker lists them.
 *
 * Roster order rather than an order of its own. It moves Sky's Memory Match
 * from 2 to 3 on this screen, which is a real cost — but it buys a game the
 * *same number on both screens*, which is the whole point of making the room
 * look like the menu she uses far more often. A room keeping a private
 * ordering is a second numbering rule, and the one used less often is the one
 * that comes to disagree.
 */
export const ROOM_GAMES: readonly RoomRoundKey[] = GAME_KEYS.filter(
  (key: GameKey): key is RoomRoundKey => key in HOW_TO_START,
)
